/* eslint-disable @typescript-eslint/naming-convention */
import { useApolloClient } from '@apollo/client';
import {
  Alert,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Link,
} from '@apollo/orbit';
import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { Form } from 'src/components/common/form/Form';
import { PrivacyPolicyLink } from 'src/components/privacyPolicyLink/PrivacyPolicyLink';
import { TermsOfServiceLink } from 'src/components/termsOfServiceLink/TermsOfServiceLink';
import { useHandleProviderSubmit } from 'src/hooks/useHandleProviderSubmit';
import { writeToLocalStorage } from 'src/hooks/useLocalStorage';
import { useLogin } from 'src/hooks/useLogin';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import type { EventCategory } from 'src/lib/analytics';
import { VALID_EMAIL } from 'src/lib/email';
import {
  IncorrectEmailOrPasswordRegex,
  IncorrectPasswordRegex,
} from 'src/lib/graphqlTypes/serverErrors';
import { getInvisibleRecaptchaToken } from 'src/lib/recaptcha';
import { relativeURL } from 'src/lib/relativeURL';
import { events } from 'src/lib/tracking';

import * as routes from '../../routes/routes';
import { SSOPage } from '../ssoPage/SSOPage';

let cookiesEnabled = true;
try {
  // Create cookie
  document.cookie = 'cookietest=1';
  const cookieExists = document.cookie.indexOf('cookietest=') !== -1;
  // Delete cookie
  document.cookie = 'cookietest=1; expires=Thu, 01-Jan-1970 00:00:01 GMT';
  if (!cookieExists) throw new Error('cookie not written');
} catch {
  cookiesEnabled = false;
}

interface Props {
  initialState?: Partial<State>;
}

interface ValidationErrors {
  email?: string;
  password?: string;
  global?: string;
}

interface State {
  email: string;
  password: string;
  errors: ValidationErrors;
}

function useClearMeAndVariantPermissionsFromCache() {
  const client = useApolloClient();

  return () => {
    client.cache.evict({
      id: 'ROOT_QUERY',
      fieldName: 'me',
    });

    // Clear variant from cache for embedded pages authentication.
    // In the embed log in flow, we might auto invite folks to a graph in the embed,
    // so they will have updated permissions that need to be fetched.
    client.cache.evict({
      id: 'ROOT_QUERY',
      fieldName: 'variant',
    });

    // Remove all dangling references to force refetches of all this data.
    client.cache.gc();
  };
}

export const LoginPage = ({ initialState }: Props) => {
  const { type } = useRouteParams(routes.login);

  const state = {
    email: '',
    password: '',
    errors: {},
    // This is so we can test state in storybook
    ...(initialState || {}),
  };
  const clearMeAndVariantPermissionsFromCache =
    useClearMeAndVariantPermissionsFromCache();
  const [email, setEmail] = useState<string>(state.email);
  const [password, setPassword] = useState<string>(state.password);
  const [errors, setErrors] = useState<ValidationErrors>(state.errors);
  const [loggingInGithub, setLoggingInGithub] = useState<boolean>(false);
  const [loggingInSSO] = useState<boolean>(false);
  const [loggingInSSOV2, setLoggingInSSOV2] = useState<boolean>(type === 'sso');
  const { from, loginError, loginErrorMessage, sessionError } = useRouteParams(
    routes.login,
  );
  const trackCustomEvent = useTrackCustomEvent();

  const history = useHistory();
  const location = useLocation();
  const [{ loggingIn }, login] = useLogin();

  const handleSignInSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();

    let hadErrors = false;
    if (!password) {
      hadErrors = true;
      setErrors({ password: 'Please provide a password.' });
      // calls tracking lib
      events.login_error.track({
        error_type: 'empty_password',
        error_message: 'No password provided.',
      });
    }
    if (!email) {
      hadErrors = true;
      setErrors((oldErrors) => ({
        ...oldErrors,
        email: 'Please provide an email address.',
      }));

      // calls tracking lib
      events.login_error.track({
        error_type: 'empty_email',
        error_message: 'No email address provided.',
      });
    } else if (!VALID_EMAIL.test(email)) {
      hadErrors = true;
      setErrors((oldErrors) => ({
        ...oldErrors,
        email: 'Please provide an email address.',
      }));

      // calls tracking lib
      events.login_error.track({
        error_type: 'invalid_email',
        error_message: 'Invalid email address.',
      });
    }

    if (!hadErrors) {
      const loginResult = await login(
        email,
        password,
        await getInvisibleRecaptchaToken({ action: 'login' }),
      );

      setErrors((oldErrors) => ({
        ...oldErrors,
        global: loginResult.error
          ? IncorrectPasswordRegex.test(loginResult.error.message)
            ? 'Incorrect password, please try another.'
            : IncorrectEmailOrPasswordRegex.test(loginResult.error.message)
              ? 'Incorrect email or password, please try again'
              : 'Error logging in.'
          : undefined,
      }));

      if (loginResult.logInSuccessful) {
        trackCustomEvent({
          action: 'sign_in_flow',
          // Old event, this style of category is deprecated
          category: 'Sign in with email button' as EventCategory,
          label: 'Studio sign in flow',
        });

        // Calls tracking lib
        events.login_auth_method_selected.track({
          authorization_method: 'email',
        });
        clearMeAndVariantPermissionsFromCache();

        // if they came from the welcome route, just push them into the app after logging in vs sending back to welcome
        const destination =
          !from || from.includes(routes.welcome.definition) ? '/' : from;

        history.push(relativeURL(destination));
      }
    }
  };

  React.useEffect(() => {
    events.login_page_viewed.track({});
  }, []);

  React.useEffect(() => {
    if (!type) return;

    // Set the default `referrer`
    writeToLocalStorage('utm', (previousValue) => ({
      ...previousValue,
      referrer: previousValue?.referrer ?? type,
    }));
  }, [type]);

  const returnToUrl = toSameOriginUrl(from) ?? window.location.origin;

  // GitHub signins should always redirect to the welcome page, so we don't
  // lose the referrer `type` in the query param. Passing `from` to the welcome
  // route will redirect to `from` when / if the privacy policy is signed
  const githubPostLoginUrl = routes.welcome.pathFrom({
    location,
    fromRouteConfig: routes.signup,
  });
  // But first we call back to validate the nonce originally passed to GitHub
  const githubCallbackUrl = routes.tokenValidate.path({
    postLogin: githubPostLoginUrl,
  });

  const handleProviderSubmitGithub = useHandleProviderSubmit({
    provider: 'github',
    callbackUrl: githubCallbackUrl,
    setLoggingIn: setLoggingInGithub,
    type,
  });

  let error;
  if (loginError) {
    if (loginErrorMessage) {
      error = `There was a problem logging in through your provider. Details: ${loginErrorMessage}`;
    } else {
      error = 'There was a problem logging in through your provider.';
    }
  } else if (sessionError) {
    error = 'Your session has expired';
  } else if (!cookiesEnabled) {
    error =
      'Studio requires cookies to log in. Please enable cookies in your browser before proceeding.';
  }

  // Any additional errors that could be returned from the login request
  if (errors.global) {
    error = errors.global;
  }

  return (
    <>
      {error && (
        <Alert className="mb-10" status="error">
          {error}
        </Alert>
      )}

      <div className="my-12">
        {!loggingInSSOV2 && (
          <Form
            onSubmit={handleSignInSubmit}
            noValidate
            className="mt-4 flex flex-col gap-4"
          >
            <FormControl isInvalid={!!errors.email}>
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                name="email"
                placeholder="Ex: name@company.com"
                value={email}
                className="w-full"
                onChange={(event) => {
                  setEmail(event.target.value);
                  setErrors((oldErrors) => ({
                    ...oldErrors,
                    email: undefined,
                  }));
                }}
              />
              <FormErrorMessage>{errors.email}</FormErrorMessage>
            </FormControl>
            <FormControl
              className="flex flex-col"
              isInvalid={!!errors.password}
            >
              <FormLabel>Password</FormLabel>
              <Input
                className="w-full"
                placeholder="Ex: K7#mP9$x"
                type="password"
                name="password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  setErrors((oldErrors) => ({
                    ...oldErrors,
                    password: undefined,
                  }));
                }}
              />
              <Link
                to={routes.forgotPassword.location({})}
                onClick={() => {
                  // Calls tracking lib
                  events.login_auth_method_selected.track({
                    authorization_method: 'forgot_password',
                  });
                }}
                className="text-sm text-secondary"
              >
                Forgot password?
              </Link>
              <FormErrorMessage>{errors.password}</FormErrorMessage>
            </FormControl>
            <div className="mt-4">
              <Button
                type="submit"
                isDisabled={loggingInGithub || loggingInSSO}
                isLoading={loggingIn}
                size="lg"
                className="w-full"
                variant="primary"
              >
                Sign in
              </Button>
            </div>
          </Form>
        )}

        {!loggingInSSOV2 && (
          <div className="flex items-center py-6">
            <hr className="m-0 flex-1 border-primary" />
            <span className="px-2 text-xs text-secondary">OR</span>
            <hr className="m-0 flex-1 border-primary" />
          </div>
        )}

        <div className="flex flex-col gap-4 pb-4">
          {!loggingInSSOV2 && (
            <Button
              type="button"
              size="lg"
              variant="secondary"
              className="mr-4 w-full"
              onClick={() => {
                handleProviderSubmitGithub();

                // calls tracking lib
                events.login_auth_method_selected.track({
                  authorization_method: 'github',
                });
              }}
              isDisabled={loggingIn || loggingInSSO || loggingInGithub}
              isLoading={loggingInGithub}
            >
              Sign in with GitHub
            </Button>
          )}
          <Button
            type="button"
            size="lg"
            variant="secondary"
            className="w-full"
            isDisabled={loggingInSSOV2}
            isLoading={loggingInSSO}
            onClick={() => {
              setLoggingInSSOV2(true);

              // calls tracking lib
              events.login_auth_method_selected.track({
                authorization_method: 'sso',
              });
            }}
          >
            {loggingInSSOV2 ? 'SSO enabled' : 'Sign in with SSO'}
          </Button>
          <div className="text-center text-sm text-secondary">
            Don't have an account?{' '}
            <ClickableText
              className="text-secondary underline"
              onClick={() => {
                // Calls tracking lib
                events.login_auth_method_selected.track({
                  authorization_method: 'signup',
                });
              }}
              as={
                <Link
                  // https://www.[studio | studio-staging | localhost:3000 | <deploy preview>].apollographql.com/signup
                  to={routes.signup.locationFrom({
                    location,
                    fromRouteConfig: routes.login,
                  })}
                />
              }
            >
              Sign up.
            </ClickableText>
          </div>
          {loggingInSSOV2 ? (
            <SSOPage
              returnToUrl={returnToUrl}
              setLoggingInSSOV2={setLoggingInSSOV2}
            />
          ) : null}
        </div>
      </div>
      <RecaptchaDisclaimer />
    </>
  );
};

// Validates that `from` is a same-origin URL before using it as `returnToUrl`
// in the SSO login request. Blocks open-redirect attacks where an attacker
// crafts a login link like ?from=https://evil.com to steal post-login redirects.
//
// Accepts relative paths (/user-settings) and absolute same-origin URLs —
// returns only the path+search+hash in both cases (backend expects a relative
// path). Returns undefined for external origins, javascript:, data:, and
// anything not parseable by new URL().
export function toSameOriginUrl(from: string | undefined): string | undefined {
  if (!from) return undefined;
  try {
    const url = new URL(from, window.location.origin);
    // Return only the path+search+hash, not the full absolute URL.
    // The backend expects a relative path as returnToUrl — returning url.href
    // (absolute) would change the existing contract and break post-login redirects.
    if (url.origin === window.location.origin)
      return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    // Invalid URL — not parseable by new URL()
  }
  return undefined;
}

// this disclaimer is required in order to hide the recaptcha badge
// https://developers.google.com/recaptcha/docs/faq#id-like-to-hide-the-recaptcha-badge.-what-is-allowed
const RecaptchaDisclaimer = () => {
  return (
    <>
      <div className="mt-6 px-14 text-center text-sm text-secondary">
        This site is protected by reCAPTCHA and the Google{' '}
        <PrivacyPolicyLink href="https://policies.google.com/privacy" /> and{' '}
        <TermsOfServiceLink href="https://policies.google.com/terms" /> apply.
      </div>
    </>
  );
};
